import { Node } from "./types";
import {
  Popover,
  Tooltip,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { 
  ContentCopy, 
  Business, 
  Add, 
  NotificationsActive,
  Visibility, 
  VisibilityOff, 
  Map,
  Streetview,
  PersonSearch,
  Description,
  DirectionsCar,
  LocationOn,
} from "@mui/icons-material";
import React from "react";
import "./HoverDataUtils.css";
import { renderMarkdownToSafeHtml } from "./HoverDataUtils";
import { getContextId } from "./sessionStorage";
import {
  buildCompanyProfileExpansionPayload,
  formatCompanyProfileExpansionError,
} from "./companyProfileExpansion";
import { hasCompanyVehicles } from "./lib/badgeUtils";
import { formatNodeAddress, getNodeCommentTarget } from "./nodeComments";
import { parseCoordinates } from "./entityProcessor";
import { useTranslation } from "react-i18next";

export type OrgGraphContextMenuProps = {
  x: number,
  y: number,
  node: Node
};

export default function OrgGraphContextMenu(props: {
  contextMenuProps: OrgGraphContextMenuProps,
  showContextMenu: boolean,
  setShowContextMenu: React.Dispatch<React.SetStateAction<boolean>>,
  onCloseComplete?: () => void,
  inlineActions?: boolean,
  actionsDisabled?: boolean,
  onShowConnectionPath?: () => void,
  isConnectionPathVisible?: boolean,
  onWatchlistChange?: (node: Node, isOnWatchList: boolean) => void,
  onHideBranch?: () => void,
  getAccessToken: () => string | undefined,
  onExpandNode?: (nodes: any[], relationships: any[], expansionNodeId: string) => void,
  onExpandVehicles?: (nodes: any[], relationships: any[], expansionNodeId: string) => void,
  setIsFetchingData?: React.Dispatch<React.SetStateAction<boolean>>,
  expandedNodes?: Set<string>,
  expandedVehicleNodes?: Set<string>,
  sourceNodeId?: string,
  enableVehicleExpansion?: boolean,
  onShowStreetView?: (location: { lat: number, lng: number } | string, address: string) => void,
  onMonitorCompany?: (orgNumber: string, displayName?: string) => Promise<void> | void,
  enableCompanyComments?: boolean,
  onSaveCompanyComment?: (node: Node, commentMarkdown: string) => Promise<void>,
  onDeleteCompanyComment?: (node: Node) => Promise<void>,
}) {
  const { t, i18n } = useTranslation();
  const { x, y, node } = props.contextMenuProps;

  const handleClose = () => {
    props.setShowContextMenu(false);
  };

  const [isCommentDialogOpen, setIsCommentDialogOpen] = React.useState(false);
  const [commentMarkdown, setCommentMarkdown] = React.useState("");
  const [commentError, setCommentError] = React.useState<string | null>(null);
  const [isSavingComment, setIsSavingComment] = React.useState(false);

  const [watchlistMembership, setWatchlistMembership] = React.useState(node.isOnWatchList);
  const [isWatchlistUpdating, setIsWatchlistUpdating] = React.useState(false);
  React.useEffect(() => { setWatchlistMembership(node.isOnWatchList); }, [node.id, node.isOnWatchList]);

  const orgName = node.properties['organization_name'] ? node.properties['organization_name'] : node.properties['organization_number'];
  const address = formatNodeAddress(node.properties);
  const addressQuery = [node.properties.street, node.properties.postal_code, node.properties.postal_address]
    .filter(value => value !== null && value !== undefined && String(value).trim())
    .map(value => String(value).trim()).join(' ');
  const addressId = String(node.properties.address_id ?? node.id);
  const addressSearchUrl = `/addresssearch?query=${encodeURIComponent(addressQuery)}`
    + (/^[a-f0-9]{32}$/i.test(addressId) ? `&addressId=${encodeURIComponent(addressId)}` : '');
  const commentTarget = getNodeCommentTarget(node);
  const existingComment = typeof node.properties['comment_markdown'] === 'string'
    ? node.properties['comment_markdown']
    : '';
  const hasExistingComment = existingComment.trim().length > 0;
  const canExpandVehicles =
    props.enableVehicleExpansion === true &&
    Boolean(props.getAccessToken()) &&
    Boolean(props.onExpandVehicles) &&
    !props.expandedVehicleNodes?.has(node.id) &&
    node.type === 'Company' &&
    hasCompanyVehicles(node.properties);
  const commentUpdatedAt = typeof node.properties['comment_updated_at'] === 'string'
    ? node.properties['comment_updated_at']
    : '';
  const canEditCompanyComment =
    props.enableCompanyComments === true &&
    commentTarget !== null &&
    (!!props.onSaveCompanyComment || !!props.onDeleteCompanyComment);
  const renderedCommentPreview = React.useMemo(
    () => renderMarkdownToSafeHtml(commentMarkdown),
    [commentMarkdown],
  );

  React.useEffect(() => {
    if (!isCommentDialogOpen) {
      return;
    }

    setCommentMarkdown(existingComment);
    setCommentError(null);
  }, [existingComment, isCommentDialogOpen, node.id]);

  React.useEffect(() => {
    if (!props.showContextMenu && !isCommentDialogOpen) {
      props.onCloseComplete?.();
    }
  }, [isCommentDialogOpen, props.onCloseComplete, props.showContextMenu]);

  const monitorCompany = (node: Node) => {
    const organizationNumber = node.properties['organization_number'];

    if (!organizationNumber || !props.onMonitorCompany) {
      return;
    }

    handleClose();

    Promise.resolve(
      props.onMonitorCompany(
        organizationNumber,
        typeof node.properties['organization_name'] === 'string'
          ? node.properties['organization_name']
          : undefined,
      ),
    ).catch((error) => {
      console.error("Error enabling graph monitoring:", error);
    });
  };

  const addToWatchlist = async (node: Node) => {
    if (isWatchlistUpdating || props.actionsDisabled) return;
    const organizationNumber = node.properties['organization_number'];
    if (!organizationNumber) {
      console.error('No organization number found for the node');
      return;
    }

    console.log('Attempting to add organization number to watchlist:', organizationNumber);

    if (!props.getAccessToken()) {
      console.warn('Authentication token is missing');
      alert(t('graph.errors.unauthenticated'));
      return;
    }

    console.log('Authentication token retrieved successfully');

    setIsWatchlistUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/lists/watchlist`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${props.getAccessToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ orgNumbers: [organizationNumber] }),
      });

      console.log('Response status:', response.status);

      if (response.status >= 400 && response.status < 500) {
        const errorMessage = await response.text();
        console.error(`Failed to add to watchlist: ${errorMessage}`);
        alert(t('graph.errors.addWatchlistDetails', { details: errorMessage }));
        return;
      }

      if (!response.ok) {
        console.error('Response not OK:', response);
        throw new Error(`Failed to add to watchlist: ${response.statusText}`);
      }

      console.log('Successfully added to watchlist:', organizationNumber);
      node.isOnWatchList = true;
      setWatchlistMembership(true);
      props.onWatchlistChange?.(node, true);
      alert(t('graph.messages.addedWatchlist', { orgNumber: organizationNumber }));
    } catch (error) {
      console.error("Error adding to watchlist:", error);
      alert(t('graph.errors.addWatchlist'));
    } finally {
      setIsWatchlistUpdating(false);
    }
  };

  const removeFromWatchlist = async (node: Node) => {
    if (isWatchlistUpdating || props.actionsDisabled) return;
    const organizationNumber = node.properties['organization_number'];
    if (!organizationNumber) {
      console.error('No organization number found for the node');
      return;
    }
    
    if (!watchlistMembership) {
      console.warn('Organization number is not on the watchlist');
      alert(t('graph.messages.notOnWatchlist', { orgNumber: organizationNumber }));
      return;
    }

    console.log('Attempting to remove organization number from watchlist:', organizationNumber);

    if (!props.getAccessToken()) {
      console.warn('Authentication token is missing');
      alert(t('graph.errors.unauthenticated'));
      return;
    }

    console.log('Authentication token retrieved successfully');

    setIsWatchlistUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/lists/watchlist/delete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${props.getAccessToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ orgNumbers: [organizationNumber] }),
      });

      console.log('Response status:', response.status);

      if (response.status >= 400 && response.status < 500) {
        const errorMessage = await response.text();
        console.error(`Failed to remove from watchlist: ${errorMessage}`);
        alert(t('graph.errors.removeWatchlistDetails', { details: errorMessage }));
        return;
      }

      if (!response.ok) {
        console.error('Response not OK:', response);
        throw new Error(`Failed to remove from watchlist: ${response.statusText}`);
      }

      console.log('Successfully removed from watchlist:', organizationNumber);
      alert(t('graph.messages.removedWatchlist', { orgNumber: organizationNumber }));

      // Update the node property to reflect the removal from the watchlist
      node.isOnWatchList = false;
      setWatchlistMembership(false);
      props.onWatchlistChange?.(node, false);
    } catch (error) {
      console.error("Error removing from watchlist:", error);
      alert(t('graph.errors.removeWatchlist'));
    } finally {
      setIsWatchlistUpdating(false);
    }
  };

  const openCommentDialog = () => {
    setCommentMarkdown(existingComment);
    setCommentError(null);
    setIsCommentDialogOpen(true);
    handleClose();
  };

  const closeCommentDialog = () => {
    if (isSavingComment) {
      return;
    }

    setIsCommentDialogOpen(false);
    setCommentError(null);
  };

  const handleCommentSave = async () => {
    if (!props.onSaveCompanyComment) {
      return;
    }

    if (!commentMarkdown.trim()) {
      setCommentError(t('graph.errors.commentEmpty'));
      return;
    }

    setIsSavingComment(true);
    setCommentError(null);

    try {
      await props.onSaveCompanyComment(node, commentMarkdown);
      setIsCommentDialogOpen(false);
    } catch (error) {
      setCommentError(error instanceof Error ? error.message : t('graph.errors.saveComment'));
    } finally {
      setIsSavingComment(false);
    }
  };

  const handleCommentDelete = async () => {
    if (!props.onDeleteCompanyComment) {
      return;
    }

    setIsSavingComment(true);
    setCommentError(null);

    try {
      await props.onDeleteCompanyComment(node);
      setIsCommentDialogOpen(false);
    } catch (error) {
      setCommentError(error instanceof Error ? error.message : t('graph.errors.deleteComment'));
    } finally {
      setIsSavingComment(false);
    }
  };

  const expandNode = async (node: Node, expandVehiclesForCompany = false) => {
    if (props.actionsDisabled) return;
    const organizationNumber = node.properties['organization_number'];
    if (!organizationNumber) {
      console.error('No organization number found for the node');
      return;
    }

    // Start fetching indicator
    if (props.setIsFetchingData) {
      props.setIsFetchingData(true);
    }

    // Get the contextId from sessionStorage
    const contextId = getContextId();
    
    if (!contextId) {
      console.error('❌ No contextId found in sessionStorage');
      console.error('❌ This means either:');
      console.error('   1. ContextId was never stored');
      console.error('   2. ContextId was cleared');
      console.error('   3. We are in a different storage context');
      
      // Clear fetching indicator
      if (props.setIsFetchingData) {
        props.setIsFetchingData(false);
      }
      
      alert(t('graph.errors.noExpansionContext'));
      return;
    }
    
    console.log('✅ Found contextId:', contextId);

    console.log('Attempting to expand node with organization number:', organizationNumber);

    if (!props.getAccessToken()) {
      console.warn('Authentication token is missing');
      
      // Clear fetching indicator
      if (props.setIsFetchingData) {
        props.setIsFetchingData(false);
      }
      
      alert(t('graph.errors.unauthenticated'));
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/companyprofiles`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${props.getAccessToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildCompanyProfileExpansionPayload(
          organizationNumber,
          contextId,
          expandVehiclesForCompany,
        )),
      });

      console.log('Expand node response status:', response.status);

      if (!response.ok) {
        const errorMessage = formatCompanyProfileExpansionError(
          response.status,
          response.statusText,
          await response.text(),
          response.headers.get('x-amzn-requestid'),
        );
        console.error(`Failed to expand node: ${errorMessage}`);
        
        // Clear fetching indicator
        if (props.setIsFetchingData) {
          props.setIsFetchingData(false);
        }
        
        alert(t('graph.errors.expandDetails', { details: errorMessage }));
        return;
      }

      const expandResponse = await response.json();
      console.log('Expansion response:', expandResponse);

      // Extract the profile data
      const profile = expandResponse.profiles && expandResponse.profiles.length > 0 
        ? expandResponse.profiles[0] 
        : null;

      if (profile && profile.graphData) {
        const newNodes = profile.graphData.nodes || [];
        const newRelationships = profile.graphData.relationships || [];
        
        console.log(`Received ${newNodes.length} new nodes and ${newRelationships.length} new relationships`);
        
        // Check if there are actually new nodes to add
        if (newNodes.length === 0 && newRelationships.length === 0) {
          // Clear fetching indicator
          if (props.setIsFetchingData) {
            props.setIsFetchingData(false);
          }
          
          alert(t('graph.messages.noNewConnections'));
          handleClose();
          return;
        }
        
        // Clear fetching indicator before starting node processing
        if (props.setIsFetchingData) {
          props.setIsFetchingData(false);
        }
        
        // Call the callback to add new nodes and relationships to the graph
        const onExpand = expandVehiclesForCompany
          ? props.onExpandVehicles
          : props.onExpandNode;

        if (onExpand) {
          onExpand(newNodes, newRelationships, node.id);
        }
        
        // Close the context menu
        handleClose();
      } else {
        console.log('No new graph data received in expansion response');
        
        // Clear fetching indicator
        if (props.setIsFetchingData) {
          props.setIsFetchingData(false);
        }
        
        alert(t('graph.messages.noAdditionalData'));
        handleClose();
      }

    } catch (error) {
      console.error("Error expanding node:", error);
      
      // Clear fetching indicator
      if (props.setIsFetchingData) {
        props.setIsFetchingData(false);
      }
      
      const errorMessage = error instanceof Error ? error.message : '';
      alert(errorMessage
        ? t('graph.errors.expandDetails', { details: errorMessage })
        : t('graph.errors.expand'));
      handleClose();
    }
  };

  const renderCompanyAction = (label: string, icon: React.ReactNode, action: () => void, disabled = false) => (
    props.inlineActions ? <Button size="small" variant="outlined" startIcon={icon} disabled={disabled || props.actionsDisabled} onClick={action}>
      {label}
    </Button> : <Tooltip title={label} arrow>
      <IconButton aria-label={label} size="small" disabled={disabled || props.actionsDisabled} onClick={action}
        sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  const companyActions = node.type === 'Company' && <>
    {props.getAccessToken() && props.onExpandNode && !props.expandedNodes?.has(node.id) && props.sourceNodeId !== node.id &&
      renderCompanyAction(t(props.inlineActions ? 'graph.investigation.expandConnections' : 'graph.expand'), <Add />, () => { void expandNode(node); })}
    {canExpandVehicles && renderCompanyAction(t('graph.expandVehicles'), <DirectionsCar />, () => { void expandNode(node, true); })}
    {node.properties.organization_number && renderCompanyAction(t('graph.openAssessment', { name: orgName }), <Business />, () => openAssessment(node))}
    {node.properties.organization_number && props.onMonitorCompany && renderCompanyAction(t('graph.monitor'), <NotificationsActive />, () => monitorCompany(node))}
    {canEditCompanyComment && renderCompanyAction(t(hasExistingComment ? 'graph.editComment' : 'graph.addComment'), <Description />, openCommentDialog)}
    {node.properties.organization_number && props.getAccessToken() && renderCompanyAction(
      t(watchlistMembership ? 'graph.removeWatchlist' : 'graph.addWatchlist'),
      watchlistMembership ? <VisibilityOff /> : <Visibility />,
      () => { void (watchlistMembership ? removeFromWatchlist(node) : addToWatchlist(node)); }, isWatchlistUpdating)}
    {node.properties.organization_number && renderCompanyAction(t('graph.copyOrgNumber', { value: node.properties.organization_number }), <ContentCopy />, () => copyToClipboard(node.properties.organization_number))}
  </>;

  return (
    <>
      {props.inlineActions ? (
        <Box role="group" aria-label={t('graph.investigation.actions')} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Button size="small" variant="outlined" aria-pressed={props.isConnectionPathVisible === true}
            disabled={props.actionsDisabled && !props.isConnectionPathVisible} onClick={props.onShowConnectionPath}>
            {t(props.isConnectionPathVisible ? 'graph.investigation.clearPath' : 'graph.investigation.showPath')}
          </Button>
          {companyActions}
          {node.type === 'Address' && addressQuery && <Button component="a" href={addressSearchUrl} target="_blank" rel="noopener noreferrer" size="small" variant="outlined" startIcon={<LocationOn />} onClick={handleClose}>{t('graph.addressSearch')}</Button>}
          {props.sourceNodeId !== node.id && <Button size="small" startIcon={<VisibilityOff />} disabled={props.actionsDisabled} onClick={props.onHideBranch}>
            {t('graph.investigation.hideBranch')}
          </Button>}
        </Box>
      ) : <Popover
        open={props.showContextMenu}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: y, left: x }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            backgroundColor: '#0f172a',
            color: 'white',
            borderRadius: '12px',
            padding: '4px 8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
            border: '1px solid #334155'
          }
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
        {companyActions}

        {node.type === 'FunktionarPerson' && node.properties['funktionar_personal_number'] && (
          <>
            <Tooltip title={t('graph.personSearch')} arrow>
              <IconButton onClick={() => handlePersonSearch(node.properties['funktionar_personal_number'])} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                <PersonSearch />
              </IconButton>
            </Tooltip>
            {canEditCompanyComment && (
              <Tooltip title={t(hasExistingComment ? 'graph.editComment' : 'graph.addComment')} arrow>
                <IconButton onClick={openCommentDialog} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                  <Description />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('graph.copyPersonalNumber', { value: node.properties['funktionar_personal_number'] })} arrow>
              <IconButton onClick={() => copyToClipboard(node.properties['funktionar_personal_number'])} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </>
        )}

        {node.type === 'Address' && (
          <>
            {addressQuery && <Tooltip title={t('graph.addressSearch')} arrow>
              <IconButton component="a" href={addressSearchUrl} target="_blank" rel="noopener noreferrer" aria-label={t('graph.addressSearch')} onClick={handleClose} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                <LocationOn />
              </IconButton>
            </Tooltip>}
            {canEditCompanyComment && (
              <Tooltip title={t(hasExistingComment ? 'graph.editComment' : 'graph.addComment')} arrow>
                <IconButton onClick={openCommentDialog} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                  <Description />
                </IconButton>
              </Tooltip>
            )}
            {props.onShowStreetView && (
              <Tooltip title={t('graph.streetView')} arrow>
                <IconButton onClick={() => {
                  if (props.onShowStreetView) {
                    let coordinates = parseCoordinates(node.properties);
                    let location: { lat: number, lng: number } | string = address;
                    if (coordinates) {
                      location = { lat: coordinates[0], lng: coordinates[1] };
                    }
                    props.onShowStreetView(location, address);
                    handleClose();
                  }
                }} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                  <Streetview />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('graph.goToAddress', { address })} arrow>
              <IconButton onClick={() => goToAddress(address)} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                <Map />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('graph.copyAddress', { address })} arrow>
              <IconButton onClick={() => copyToClipboard(address)} size="small" sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </>
        )}
        </Stack>
      </Popover>}
      <Dialog
        open={isCommentDialogOpen}
        onClose={closeCommentDialog}
        fullWidth
        maxWidth="md"
      >
      <DialogTitle>
        {t(hasExistingComment ? 'graph.editComment' : 'graph.addComment')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            {commentTarget && (
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
                {t(`graph.${commentTarget.nodeType}`, { defaultValue: commentTarget.typeLabel })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {commentTarget?.displayLabel || orgName}
            </Typography>
            {commentUpdatedAt && hasExistingComment && (
              <Typography variant="caption" color="text.secondary">
                {t('graph.lastUpdated', { date: new Date(commentUpdatedAt).toLocaleString(i18n.language === 'sv' ? 'sv-SE' : 'en-SE') })}
              </Typography>
            )}
          </Box>

          <TextField
            label={t('common.fields.comment')}
            multiline
            minRows={8}
            value={commentMarkdown}
            onChange={(event) => setCommentMarkdown(event.target.value)}
            disabled={isSavingComment}
            helperText={t('graph.commentTips')}
            fullWidth
          />

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              backgroundColor: 'background.default',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('graph.preview')}
            </Typography>
            {commentMarkdown.trim() ? (
              <div
                className="graph-comment-markdown"
                dangerouslySetInnerHTML={{ __html: renderedCommentPreview }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('graph.nothingToPreview')}
              </Typography>
            )}
          </Box>

          {commentError && (
            <Typography variant="body2" color="error">
              {commentError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCommentDialog} disabled={isSavingComment}>
          {t('common.actions.cancel')}
        </Button>
        {hasExistingComment && props.onDeleteCompanyComment && (
          <Button
            onClick={handleCommentDelete}
            color="error"
            disabled={isSavingComment}
          >
            {isSavingComment ? <CircularProgress size={18} color="inherit" /> : t('common.actions.delete')}
          </Button>
        )}
        {props.onSaveCompanyComment && (
          <Button
            onClick={handleCommentSave}
            variant="contained"
            disabled={isSavingComment || !commentMarkdown.trim()}
          >
            {isSavingComment ? <CircularProgress size={18} color="inherit" /> : t('common.actions.save')}
          </Button>
        )}
      </DialogActions>
      </Dialog>
    </>
  );
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text)
}

function openAssessment(node: Node) {
  var url = `/companyassessment/${node.properties['organization_number']}`;
  const newTab = window.open(url, '_blank');
  if (newTab) {
    newTab.opener = null;
  }
}

function goToAddress(address: string) {
  if (address) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank')
  } else {
    console.error('No address to go to');
  }
}

function handlePersonSearch(pnr: string) {
  if (!pnr) return;
  let fullPnr = pnr;
  if (fullPnr.length === 10) {
    const year = parseInt(fullPnr.substring(0, 2));
    const currentYearShort = parseInt(new Date().getFullYear().toString().substring(2));
    // Simple heuristic: if year > 26 (current year), assume 19YY. Else 20YY.
    const century = year > currentYearShort ? '19' : '20';
    fullPnr = century + fullPnr;
  }
  window.open(`/personsearch?pnr=${fullPnr}`, '_blank');
}
